import greenTick from 'assets/svg/green-tick.svg';
import BusinessIndustryPicker from 'components/shared/BusinessIndustryPicker';
import BasicModal from 'components/core/Modal';
import {
    useCreateSubscriptionMutation,
    useGetBusinessSuscriptionPlanQuery,
    useGetSubscriptionStatusQuery,
    useCancelSubscriptionMutation,
} from 'redux/services/spotdif/subscriptionPlan';
import { useFormikContext } from 'formik';
import React from 'react';
import { format } from 'date-fns';
import './BusinessSubscriptionPlan.scss';

import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import SpinningComponent from 'components/shared/SpinningComponent';
import useAuthentication from 'hooks/useAuthentication';
import { useAppDispatch } from 'redux/store';
import { spotdifApi } from 'redux/services/spotdif';
import { TAG_USER_PROFILE, TAG_GET_USER_PROFILE } from 'contracts/spotDiffAPITags';
import { IBusinessSubscriptionPlan } from 'contracts/view-models/IBusinessSubscriptionPlan';
import MDTypography from 'components/themed/MDTypography';

interface FormValues {
    businessIndustry?: string;
    subscriptionId?: string;
    acceptTerms?: boolean;
    // add other form fields here if needed
}

const BusinessSubscriptionPlan = () => {
    const { user } = useAuthentication();
    const { values, setFieldValue, errors, touched } = useFormikContext<FormValues>();
    const [createSubscription] = useCreateSubscriptionMutation();
    const [cancelSubscription] = useCancelSubscriptionMutation();
    const appDispatch = useAppDispatch();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedPlan, setSelectedPlan] = React.useState<any>(null);
    const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
    const [subscriptionId, setSubscriptionId] = React.useState<string | null>(null);
    const stripe = useStripe();
    const elements = useElements();
    const { enqueueSnackbar } = useSnackbar();

    const { data } = useGetBusinessSuscriptionPlanQuery(
        { id: values.businessIndustry as string, type: 'true' },
        { skip: !values.businessIndustry },
    );

    // Helper function to get plan comparison info
    const getPlanComparisonInfo = (plan: IBusinessSubscriptionPlan) => {
        if (!user?.activeSubscriptionId || user.activeSubscriptionId.planId === plan._id) {
            let isPaused = user?.activeSubscriptionId?.stripeSubscriptionStatus === 'past_due';

            return {
                isCurrentPlan: user?.activeSubscriptionId?.planId === plan._id,
                isLowerPlan: false,
                isUpgrade: false,
                currentPlan: null,
                isPaused,
            };
        }

        const currentPlan = data?.data?.find((p) => p._id === user.activeSubscriptionId?.planId);
        const isLowerPlan = currentPlan && plan.planPrice < currentPlan.planPrice;
        // const isUpgrade = currentPlan && plan.planPrice > currentPlan.planPrice;
        const isUpgrade = currentPlan && plan.planPrice > currentPlan.planPrice;

        return {
            isCurrentPlan: false,
            isLowerPlan: Boolean(isLowerPlan),
            isUpgrade: Boolean(isUpgrade),
            currentPlan,
        };
    };

    // Helper function to get button text
    // const getButtonText = (plan: IBusinessSubscriptionPlan) => {
    //     const { isCurrentPlan, isLowerPlan, isUpgrade, isPaused } = getPlanComparisonInfo(plan);

    //     if (isCurrentPlan) {
    //         if (isPaused) {
    //             return 'Subscription Paused';
    //         }
    //         return 'Active';
    //     }
    //     // if (values.subscriptionId === plan._id) return 'Selected';
    //     // if (isLowerPlan) return 'Lower Plan';
    //     // if (isUpgrade) return 'Upgrade';
    //     return 'Get Started';
    // };

    // Helper function to check if button should be disabled
    const isButtonDisabled = (plan: IBusinessSubscriptionPlan) => {
        const { isCurrentPlan, isLowerPlan, isUpgrade } = getPlanComparisonInfo(plan);
        // return isCurrentPlan ;

        console.log(isCurrentPlan, ">>>> isCurrentPlan");
        return !isCurrentPlan && (isLowerPlan || isUpgrade);
    };

    const {
        data: subscriptionStatusData,
        isLoading: isPollingStatus,
        refetch: refetchStatus,
    } = useGetSubscriptionStatusQuery(
        { id: subscriptionId! },
        {
            skip: !subscriptionId,
            pollingInterval: 3000, // Poll every 3 seconds
        },
    );
    React.useEffect(() => {
        if (
            subscriptionStatusData &&
            (subscriptionStatusData.data.subscriptionStatus === 'trialing' ||
                subscriptionStatusData.data.subscriptionStatus === 'active')
        ) {
            const endDate = subscriptionStatusData.data.currentPeriodEnd
                ? format(new Date(subscriptionStatusData.data.currentPeriodEnd * 1000), 'MMM, d, yyyy')
                : 'soon';
            enqueueSnackbar(`Your subscription will start on ${endDate} `, {
                variant: 'success',
                key: 'subscription-success',
                autoHideDuration: 8000,
            });

            // Invalidate the currentUserData query to refresh user data
            appDispatch(
                spotdifApi.util.invalidateTags([
                    {
                        type: TAG_USER_PROFILE,
                        id: TAG_GET_USER_PROFILE,
                    },
                ]),
            );

            // Stop polling and reset state
            setSubscriptionId(null);
            setIsProcessingPayment(false);
            handleCloseModal();
        }
    }, [subscriptionStatusData, enqueueSnackbar, appDispatch]);

    const handleOpenModal = (plan: any) => {
        setFieldValue('subscriptionId', plan._id);
        // setSelectedPlan(plan);
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPlan(null);
        setIsProcessingPayment(false);
        setSubscriptionId(null);
    };

    const handleCreateSubscription = async () => {
        if (!stripe || !elements) return;

        setIsProcessingPayment(true);
        let createdSubscriptionId: string | null = null;

        try {
            const response = await createSubscription({ planId: values.subscriptionId }).unwrap();
            const setupIntentClientSecret = response?.data?.setup_intent;
            const paymentIntentClientSecret = response?.data?.payment_intent;
            const responseSubscriptionId = response?.data?.subscriptionId;
            const paymentType = response?.data?.paymentType;
            const requiresAction = response?.data?.requiresAction;
            const subscriptionStatus = response?.data?.subscriptionStatus;

            createdSubscriptionId = responseSubscriptionId; // Store for potential cleanup
            console.log('Payment Type:', paymentType);
            console.log('Requires Action:', requiresAction);
            console.log('Subscription Status:', subscriptionStatus);

            // Handle different payment scenarios
            // if (!requiresAction) {
            //     // Subscription is active, no payment method needed (e.g., trial period)
            //     console.log('Subscription created successfully, no immediate payment required');
            //     setSubscriptionId(responseSubscriptionId || '');
            //     setIsProcessingPayment(false);
            //     enqueueSnackbar('Subscription created successfully!', { variant: 'success' });
            //     return;
            // }

            // Payment action is required
            if (paymentType === 'setup') {
                // Handle setup intent for future payments (trial period)
                if (!setupIntentClientSecret) {
                    setIsProcessingPayment(false);
                    enqueueSnackbar('Failed to create subscription setup', { variant: 'error' });
                    return;
                }

                const result = await stripe.confirmCardSetup(setupIntentClientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement)!,
                    },
                });

                if (result.error) {
                    console.error('Setup failed:', result.error.message);
                    setIsProcessingPayment(false);
                    handlePaymentError(result.error, createdSubscriptionId);
                } else {
                    console.log('Payment method setup completed for future billing.');
                    setSubscriptionId(responseSubscriptionId || '');
                    enqueueSnackbar('Payment method saved successfully! Subscription is active.', {
                        variant: 'success',
                    });
                }
            } else if (paymentType === 'payment') {
                // Handle payment intent for immediate payment
                if (!paymentIntentClientSecret) {
                    setIsProcessingPayment(false);
                    enqueueSnackbar('Failed to create subscription payment', { variant: 'error' });
                    return;
                }

                const result = await stripe.confirmCardPayment(paymentIntentClientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement)!,
                    },
                });

                if (result.error) {
                    console.error('Payment failed:', result.error.message);
                    setIsProcessingPayment(false);
                    handlePaymentError(result.error, createdSubscriptionId);
                } else {
                    console.log('Payment completed successfully.');
                    setSubscriptionId(responseSubscriptionId || '');
                    enqueueSnackbar('Payment processed successfully! Subscription is active.', { variant: 'success' });
                }
            } else {
                // paymentType === 'none' - shouldn't happen if requiresAction is true, but handle it
                console.log('No payment action required.');
                setSubscriptionId(responseSubscriptionId || '');
                setIsProcessingPayment(false);
                enqueueSnackbar('Subscription created successfully!', { variant: 'success' });
            }
        } catch (error: any) {
            setIsProcessingPayment(false);
            enqueueSnackbar(error?.data?.error?.message || 'An error occurred while processing payment', {
                variant: 'error',
            });
            handleCloseModal();
            // If subscription was created but there was an error, try to cancel it
            if (createdSubscriptionId) {
                try {
                    await cancelSubscription({ id: createdSubscriptionId }).unwrap();
                } catch (cancelError) {
                    console.error('Failed to cancel subscription:', cancelError);
                }
            }
        }

        // try {
        //     const response = await createSubscription({ planId: values.subscriptionId }).unwrap();
        //     const setupIntentClientSecret = response?.data?.setup_intent;
        //     const responseSubscriptionId = response?.data?.subscriptionId;
        //     createdSubscriptionId = responseSubscriptionId; // Store for potential cleanup
        //     console.log('Setup Intent Client Secret:', setupIntentClientSecret);

        //     if (!setupIntentClientSecret) {
        //         setIsProcessingPayment(false);
        //         enqueueSnackbar('Failed to create subscription', { variant: 'error' });
        //         return;
        //     }

        //     const result = await stripe.confirmCardSetup(setupIntentClientSecret, {
        //         payment_method: {
        //             card: elements.getElement(CardElement)!,
        //         },
        //     });
        //     if (result.error) {
        //         console.error('Setup failed:', result.error.message);
        //         setIsProcessingPayment(false);

        //         // Provide specific error messages for common decline reasons
        //         let errorMessage = `Payment failed: ${result.error.message}`;
        //         if (result.error.code === 'card_declined') {
        //             errorMessage =
        //                 'Your card was declined. Please check your card details or try a different payment method.';
        //         } else if (result.error.code === 'incorrect_cvc') {
        //             errorMessage = "Your card's security code is incorrect. Please check and try again.";
        //         } else if (result.error.code === 'expired_card') {
        //             errorMessage = 'Your card has expired. Please use a different payment method.';
        //         } else if (result.error.code === 'processing_error') {
        //             errorMessage = 'An error occurred while processing your card. Please try again.';
        //         }

        //         enqueueSnackbar(errorMessage, { variant: 'error' });

        //         // Cancel the subscription since payment failed
        //         if (createdSubscriptionId) {
        //             try {
        //                 await cancelSubscription({ id: createdSubscriptionId }).unwrap();
        //                 console.log('Subscription cancelled due to payment failure');
        //             } catch (cancelError) {
        //                 console.error('Failed to cancel subscription:', cancelError);
        //                 // Still show the payment error to user, but log the cancel error
        //             }
        //         }
        //     } else {
        //         console.log('Payment method attached, subscription setup done.');
        //         // Start polling by setting the subscription ID from the response
        //         setSubscriptionId(responseSubscriptionId || '');
        //         enqueueSnackbar('Payment processed successfully! Checking subscription status...', { variant: 'info' });
        //     }
        // } catch (error: any) {
        //     setIsProcessingPayment(false);
        //     enqueueSnackbar(error?.data?.error?.message || 'An error occurred while processing payment', {
        //         variant: 'error',
        //     });
        //     handleCloseModal();
        //     // If subscription was created but there was an error, try to cancel it
        //     if (createdSubscriptionId) {
        //         try {
        //             await cancelSubscription({ id: createdSubscriptionId }).unwrap();
        //         } catch (cancelError) {
        //             console.error('Failed to cancel subscription:', cancelError);
        //         }
        //     }
        // }
    };

    const handlePaymentError = async (error: any, subscriptionId: string) => {
        // Provide specific error messages for common decline reasons
        let errorMessage = `Payment failed: ${error.message}`;
        if (error.code === 'card_declined') {
            errorMessage = 'Your card was declined. Please check your card details or try a different payment method.';
        } else if (error.code === 'incorrect_cvc') {
            errorMessage = "Your card's security code is incorrect. Please check and try again.";
        } else if (error.code === 'expired_card') {
            errorMessage = 'Your card has expired. Please use a different payment method.';
        } else if (error.code === 'processing_error') {
            errorMessage = 'An error occurred while processing your card. Please try again.';
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });

        // Cancel the subscription since payment failed
        if (subscriptionId) {
            try {
                await cancelSubscription({ id: subscriptionId }).unwrap();
                console.log('Subscription cancelled due to payment failure');
            } catch (cancelError) {
                console.error('Failed to cancel subscription:', cancelError);
                // Still show the payment error to user, but log the cancel error
            }
        }
    };

    const handleCreateSubscriptionWithTrial = async (plan: IBusinessSubscriptionPlan) => {
        await createSubscription({ planId: plan._id })
            .unwrap()
            .then((response) => {
                appDispatch(
                    spotdifApi.util.invalidateTags([
                        {
                            type: TAG_USER_PROFILE,
                            id: TAG_GET_USER_PROFILE,
                        },
                    ]),
                );
                enqueueSnackbar(response.message ?? 'Subscription created successfully!', { variant: 'success' });
            })
            .catch((error) => {
                console.error('Error creating subscription with trial:', error);
                enqueueSnackbar(error?.data?.error?.message || 'An error occurred while creating subscription', {
                    variant: 'error',
                });
            });
    };

    return (
        <div className="tab-user-content-card business-subscription-plan">
            <div className="industry-picker">
                <BusinessIndustryPicker fieldName="businessIndustry" />
            </div>
            <div className="subscripitons">
                {data?.data?.length ? (
                    data.data.map((plan, index) => {
                        return (
                            <>
                                <div
                                    className={`subcription-card ${
                                        user?.activeSubscriptionId?.planId === plan._id ? 'active-subscription' : ''
                                    }`}
                                    key={index}
                                >
                                    {user?.activeSubscriptionId?.planId === plan._id &&
                                        user?.activeSubscriptionId?.stripeSubscriptionStatus === 'trialing' && (
                                            <div className="sub-card-start-badge">
                                                Subscription will start from{' '}
                                                {format(new Date(user?.activeSubscriptionId?.endDate), 'd MMMM')}
                                            </div>
                                        )}
                                    <h2 className="sub-card-heading">{plan.name}</h2>
                                    <div className="sub-card-pricing">
                                        {!plan.isSystemTrial && (
                                            <div className="pricing">
                                                <h3>£ {plan.planPrice}</h3>
                                                <span>+ VAT</span>
                                            </div>
                                        )}
                                        <p>Additional appts £{plan.additionalAppointmentPrice} each</p>
                                    </div>
                                    <div className="sub-card-description">
                                        {plan.isSystemTrial ? (
                                            <>
                                                <div>
                                                    <img src={greenTick} />
                                                    <span>
                                                        Signup for <strong>only {plan.planAppointments} free</strong>{' '}
                                                        appointment
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    <img src={greenTick} />
                                                    <span>First 5 appointments free</span>
                                                </div>
                                                <div>
                                                    <img src={greenTick} />
                                                    <span>Guranteed {plan.planAppointments} appointments/ Month</span>
                                                </div>
                                                <div>
                                                    <img src={greenTick} />
                                                    <span>Monthly subscription on direct debit</span>
                                                </div>
                                                <div>
                                                    <img src={greenTick} />
                                                    <span>12 Month Contract</span>
                                                </div>
                                            </>
                                        )}
                                    </div>{' '}
                                    <div className="sub-card-button">
                                        <button
                                            onClick={() => {
                                                // Prevent action if button is active (current subscription)
                                                if (user?.activeSubscriptionId?.planId === plan._id) {
                                                    return;
                                                }

                                                if (plan.isSystemTrial) {
                                                    handleCreateSubscriptionWithTrial(plan);
                                                    return;
                                                }

                                                handleOpenModal(plan);
                                            }}
                                            className={`btn ${
                                                values.subscriptionId === plan._id ? 'btn-selected' : 'btn-primary'
                                            } ${isButtonDisabled(plan) ? 'btn-disabled' : ''}`}
                                            type="button"
                                            disabled={isButtonDisabled(plan)}
                                        >
                                            Get Started
                                        </button>
                                    </div>
                                </div>
                            </>
                        );
                    })
                ) : (
                    <div className="no-packages">
                        <p>No packages available</p>
                    </div>
                )}
            </div>
            {touched.subscriptionId && errors.subscriptionId && (
                <div className="subscription-error">
                    <span className="error-message">{errors.subscriptionId}</span>
                </div>
            )}
            <BasicModal open={isModalOpen} handleClose={handleCloseModal}>
                <div className="subscription-modal">
                    <div className="modal-header">
                        <h2>Complete Your Subscription</h2>
                    </div>

                    <div className="modal-content">
                        {' '}
                        <div className="payment-form">
                            <div className="form-group">
                                <label className="form-label">Payment Information</label>
                                <div className="card-input-wrapper">
                                    <CardElement
                                        className="custom-stripe-card-element"
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#1f2937',
                                                    letterSpacing: '0.025em',
                                                    fontFamily:
                                                        '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                                                    '::placeholder': {
                                                        color: '#9ca3af',
                                                        fontWeight: '400',
                                                    },
                                                    iconColor: '#6b7280',
                                                },
                                                invalid: {
                                                    color: '#ef4444',
                                                    iconColor: '#ef4444',
                                                },
                                                complete: {
                                                    iconColor: '#10b981',
                                                },
                                            },
                                            hidePostalCode: false,
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="terms-section">
                                <div className="terms-checkbox">
                                    <input
                                        type="checkbox"
                                        id="acceptTerms"
                                        checked={values.acceptTerms || false}
                                        onChange={(e) => setFieldValue('acceptTerms', e.target.checked)}
                                        className={`terms-checkbox-input ${
                                            touched.acceptTerms && errors.acceptTerms ? 'error' : ''
                                        }`}
                                    />
                                    <label htmlFor="acceptTerms" className="terms-text">
                                        I agree that by providing my card information, you may charge my card for future payments
                                        in accordance with the{' '}
                                        <MDTypography
                                            component="a"
                                            href="https://leads.spotdif.com/terms&conditions/Narwhal-Ts&Cs.pdf"
                                            target="_blank"
                                            variant="button"
                                            fontWeight="bold"
                                            color="info"
                                            textGradient
                                        >
                                            Terms and Conditions
                                        </MDTypography>
                                    </label>
                                </div>
                                {touched.acceptTerms && errors.acceptTerms && (
                                    <div className="terms-error">
                                        <span className="error-message">{errors.acceptTerms}</span>
                                    </div>
                                )}
                            </div>
                        </div>{' '}
                        <div className="modal-actions">
                            {isProcessingPayment && (
                                <div className="processing-status">
                                    <SpinningComponent loading={true} isFullSized={false}>
                                        <div />
                                    </SpinningComponent>
                                    <p>Processing your payment...</p>
                                </div>
                            )}
                            <button
                                className="btn-subscribe"
                                onClick={handleCreateSubscription}
                                disabled={!stripe || isProcessingPayment || !values.acceptTerms}
                            >
                                {!stripe ? 'Loading...' : isProcessingPayment ? 'Processing...' : 'Subscribe Now'}
                            </button>
                        </div>
                    </div>
                </div>
            </BasicModal>
        </div>
    );
};
export default BusinessSubscriptionPlan;
